import type { GameSettings } from '../game/settings';
import { normaliseCode } from '../game/settings';
import { KoodooScene, TEXT_STYLES } from './base-scene';

type ToggleKey = 'music' | 'soundEffects' | 'playerVoices';

interface ToggleRow {
  key: ToggleKey;
  label: string;
  y: number;
}

/** Rows mirror `docs/settings-mockup.png`, top to bottom. */
export const TOGGLE_ROWS: readonly ToggleRow[] = [
  { key: 'music', label: 'MUSIC', y: 205 },
  { key: 'soundEffects', label: 'SOUND EFFECTS', y: 312 },
  { key: 'playerVoices', label: 'PLAYER VOICES', y: 418 },
];

const LABEL_X = 326;
const TOGGLE_X = 860;

export class SettingsScene extends KoodooScene {
  static readonly KEY = 'Settings';

  private toggles = new Map<ToggleKey, Phaser.GameObjects.Image>();

  constructor() {
    super(SettingsScene.KEY);
  }

  preload(): void {
    this.loadBackgrounds();
    this.load.image('back_button', 'assets/images/ui-objects/back-icon.png');
    this.load.image('toggle_on', 'assets/images/ui-objects/toggle-on.png');
    this.load.image('toggle_off', 'assets/images/ui-objects/toggle-off.png');
  }

  create(): void {
    this.playMenuTheme();
    // Phaser reuses scene instances across `scene.start`; clear the previous
    // visit's destroyed game objects before repopulating.
    this.toggles.clear();

    this.addBackgrounds();
    this.setCursor('generic');

    this.add.text(LABEL_X, 60, 'SETTINGS', TEXT_STYLES.menu);

    for (const row of TOGGLE_ROWS) {
      this.addToggleRow(row);
    }

    this.addCodeEntry();
    this.addBackButton();
    this.refresh(this.session.getSettings());
  }

  private refresh(settings: GameSettings): void {
    for (const [key, image] of this.toggles) {
      image.setTexture(settings[key] ? 'toggle_on' : 'toggle_off');
    }
  }

  private addToggleRow(row: ToggleRow): void {
    this.add.text(LABEL_X, row.y, row.label, TEXT_STYLES.menu).setOrigin(0, 0.5);

    const toggle = this.add.image(TOGGLE_X, row.y, 'toggle_off').setInteractive();
    this.toggles.set(row.key, toggle);

    toggle.on('pointerover', () => this.session.audio.playEffect('mouse_over'));
    toggle.on('pointerup', () => {
      const current = this.session.getSettings();
      // Read the effect before flipping, so muting still plays its own click.
      this.session.audio.playEffect('mouse_click');
      this.refresh(this.session.updateSettings({ [row.key]: !current[row.key] }));
    });
  }

  /**
   * Phaser cannot render a text input, so the unlock code is collected through
   * a browser prompt. Replaced by a proper DOM overlay in a later phase.
   */
  private addCodeEntry(): void {
    const current = this.session.getSettings().code;
    const label = this.add
      .text(LABEL_X, 523, `ENTER CODE: ${current ?? '______'}`, TEXT_STYLES.menu)
      .setOrigin(0, 0.5)
      .setInteractive();

    label.on('pointerup', () => {
      const entered = window.prompt('Enter code:', current ?? '');
      if (entered === null) {
        return;
      }
      const code = normaliseCode(entered);
      this.session.updateSettings({ code });
      label.setText(`ENTER CODE: ${code ?? '______'}`);
    });
  }

  private addBackButton(): void {
    const back = this.add.image(80, 640, 'back_button').setInteractive();
    back.setAlpha(0.8);
    back.on('pointerover', () => {
      back.setAlpha(1);
      this.session.audio.playEffect('mouse_over');
    });
    back.on('pointerout', () => back.setAlpha(0.8));
    back.on('pointerup', () => this.scene.start('MainMenu'));
  }
}
